import React, { useEffect, useState } from "react";
import { Edit, Trash2, Eye, Download, FileText } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import "./Collections.css";

const url = process.env.REACT_APP_BACKEND_URL;

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [houseTypes, setHouseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterHouseType, setFilterHouseType] = useState("");
  const [filterHouseNo, setFilterHouseNo] = useState("");
  const [filterZone, setFilterZone] = useState("");
  const [filterCollector, setFilterCollector] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // Yes/No filter
  const navigate = useNavigate();

  // ✅ Fetch collections
  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${url}/api/collection`);
      setCollections(res.data.data || []);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to fetch collections");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch house types
  const fetchHouseTypes = async () => {
    try {
      const res = await axios.get(`${url}/api/housetype`);
      setHouseTypes(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch house types");
    }
  };

  useEffect(() => {
    fetchCollections();
    fetchHouseTypes();
  }, []);

  // ✅ Delete collection
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this collection?")) {
      try {
        await axios.delete(`${url}/api/collection/${id}`);
        fetchCollections();
        alert("Collection deleted successfully");
      } catch (err) {
        alert(err.response?.data?.message || "Failed to delete collection");
      }
    }
  };

  // ✅ Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // ✅ Format date for Excel
  const formatDateForExcel = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // ✅ Export to Excel
  const exportToExcel = () => {
    const excelData = filteredCollections.map((c, index) => ({
      "S.No": index + 1,
      "House No": c.houseNo,
      "Customer Name": c.customer?.customerName || "N/A",
      "Collector": `${c.collector?.fname || ""} ${c.collector?.lname || ""}`.trim() || "N/A",
      "Status": c.collectionStatus || "N/A",
      "Collected At": formatDateForExcel(c.collectedAt),
      "House Type": c.customer?.houseType?.name || "N/A",
      "Zone": c.customer?.zone?.name || "N/A",
      "Phone": c.customer?.phone || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Collections");
    
    const date = new Date();
    const fileName = `collections_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ✅ Export Summary
  const exportSummary = () => {
    const yesCount = filteredCollections.filter(c => c.collectionStatus === "Yes").length;
    const noCount = filteredCollections.filter(c => c.collectionStatus === "No").length;
    
    const summaryData = [{
      "Report Date": new Date().toLocaleDateString(),
      "Total Collections": filteredCollections.length,
      "Collected (Yes)": yesCount,
      "Not Collected (No)": noCount,
      "Collection Rate": filteredCollections.length > 0 
        ? `${((yesCount / filteredCollections.length) * 100).toFixed(1)}%` 
        : "0%"
    }];

    const ws = XLSX.utils.json_to_sheet(summaryData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
    
    const date = new Date();
    const fileName = `summary_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ✅ Filters
  const filteredCollections = collections.filter((c) => {
    const matchHouseType = filterHouseType
      ? c.customer?.houseType?._id === filterHouseType
      : true;

    const matchHouseNo = filterHouseNo
      ? String(c.houseNo || "").toLowerCase().includes(filterHouseNo.toLowerCase())
      : true;

    const matchZone = filterZone
      ? (c.customer?.zone?.name || "").toLowerCase().includes(filterZone.toLowerCase())
      : true;

    const matchCollector = filterCollector
      ? (c.collector?.fname || "").toLowerCase().includes(filterCollector.toLowerCase()) ||
        (c.collector?.lname || "").toLowerCase().includes(filterCollector.toLowerCase())
      : true;

    const matchStatus = filterStatus
      ? c.collectionStatus === filterStatus
      : true;

    return matchHouseType && matchHouseNo && matchZone && matchCollector && matchStatus;
  });

  const yesCount = filteredCollections.filter(c => c.collectionStatus === "Yes").length;
  const noCount = filteredCollections.filter(c => c.collectionStatus === "No").length;

  if (loading) return <div className="loading">Loading collections...</div>;

  return (
    <div className="collections-container">
      {/* Header with Export Buttons */}
      <div className="header-section">
        <h1 className="page-title">Collections List</h1>
        <div className="action-buttons">
          <button className="summary-btn" onClick={exportSummary}>
            <FileText size={16} />
            Summary Report
          </button>
          <button className="export-btn" onClick={exportToExcel}>
            <Download size={16} />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <select
          className="filter-select"
          value={filterHouseType}
          onChange={(e) => setFilterHouseType(e.target.value)}
        >
          <option value="">All House Types</option>
          {houseTypes.map((h) => (
            <option key={h._id} value={h._id}>
              {h.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="filter-input"
          placeholder="Search by House No"
          value={filterHouseNo}
          onChange={(e) => setFilterHouseNo(e.target.value)}
        />

        <input
          type="text"
          className="filter-input"
          placeholder="Search by Zone"
          value={filterZone}
          onChange={(e) => setFilterZone(e.target.value)}
        />

        <input
          type="text"
          className="filter-input"
          placeholder="Search by Collector"
          value={filterCollector}
          onChange={(e) => setFilterCollector(e.target.value)}
        />

        {/* ✅ New Yes/No Filter Dropdown */}
        <select
          className="filter-select status-filter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="Yes" className="option-yes">Yes (Collected)</option>
          <option value="No" className="option-no">No (Not Collected)</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{filteredCollections.length}</div>
          <div className="stat-label">Total Collections</div>
        </div>
        <div className="stat-card yes">
          <div className="stat-value">{yesCount}</div>
          <div className="stat-label">Collected (Yes)</div>
        </div>
        <div className="stat-card no">
          <div className="stat-value">{noCount}</div>
          <div className="stat-label">Not Collected (No)</div>
        </div>
        <div className="stat-card rate">
          <div className="stat-value">
            {filteredCollections.length > 0 
              ? `${((yesCount / filteredCollections.length) * 100).toFixed(1)}%` 
              : "0%"}
          </div>
          <div className="stat-label">Collection Rate</div>
        </div>
      </div>

      {/* Collections Table */}
      <div className="table-container">
        <table className="collections-table">
          <thead>
            <tr>
              <th>House No</th>
              <th>Customer Name</th>
              <th>Collector</th>
              <th>Status</th>
              <th>Collected At</th>
              <th>House Type</th>
              <th>Zone</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCollections.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No collections found
                </td>
              </tr>
            ) : (
              filteredCollections.map((c) => (
                <tr key={c._id}>
                  <td>{c.houseNo}</td>
                  <td>{c.customer?.customerName || "N/A"}</td>
                  <td>
                    {c.collector?.fname} {c.collector?.lname}
                  </td>
                  <td>
                    <span className={`status-badge ${c.collectionStatus === 'Yes' ? 'yes' : 'no'}`}>
                      {c.collectionStatus || "N/A"}
                    </span>
                  </td>
                  <td>{formatDate(c.collectedAt)}</td>
                  <td>{c.customer?.houseType?.name || "N/A"}</td>
                  <td>{c.customer?.zone?.name || "N/A"}</td>
                  <td>
                    <div className="action-buttons-cell">
                      <button
                        onClick={() => navigate(`/collections/view/${c._id}`)}
                        className="icon-btn view"
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/collections/edit/${c._id}`)}
                        className="icon-btn edit"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(c._id)}
                        className="icon-btn delete"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Collections;